import { Question } from "../types";

/** Odstraní všechny @ z hodnoty incidentu při porovnávání. */
export function stripIncidentMarker(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/@/g, "")
    .trim();
}

export function hasIncidentAutoAlertMarker(value: unknown): boolean {
  return String(value ?? "").includes("@");
}

export function normalizeAnswerCompareValue(value: unknown): string {
  return stripIncidentMarker(String(value).trim().replace(",", "."));
}

type AnswerLike = Record<string, unknown>;

export function getAnswerValueCandidates(question: Question, answer: AnswerLike): string[] {
  switch (question.type) {
    case "boolean":
      return answer?.val_boolean == null ? [] : [String(Boolean(answer.val_boolean))];
    case "text":
      return answer?.val_text != null ? [String(answer.val_text)] : [];
    case "number":
      return answer?.val_number != null ? [String(answer.val_number)] : [];
    case "real":
      return answer?.val_real != null ? [String(answer.val_real)] : [];
    case "lov":
      return answer?.val_lov != null ? [String(answer.val_lov)] : [];
    case "date":
    case "datetime":
      return answer?.val_text != null ? [String(answer.val_text)] : [];
    default:
      return [];
  }
}

export function parseIncidentValues(incidentRaw?: string): string[] {
  if (!incidentRaw) return [];
  const incident = String(incidentRaw).trim();
  if (!incident) return [];

  if (incident.startsWith("[") && incident.endsWith("]")) {
    try {
      const normalized = incident.replace(/'/g, '"');
      const parsed = JSON.parse(normalized);
      if (Array.isArray(parsed)) {
        if (parsed.length > 0 && typeof parsed[0] === "object" && parsed[0] != null) {
          return parsed
            .map((item: { value?: unknown }) => item?.value)
            .filter((x): x is string | number | boolean => x != null)
            .map((x) => String(x).trim())
            .filter(Boolean);
        }
        return parsed.map((x) => String(x).trim()).filter(Boolean);
      }
    } catch {
      // ignore and use fallback parser
    }
  }

  return incident
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .split(",")
    .map((x) => x.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean);
}

export function parseIncidentTaskValues(incidentRaw?: string): string[] {
  if (!incidentRaw) return [];
  const incident = String(incidentRaw).trim();
  if (!incident) return [];

  if (incident.startsWith("[") && incident.endsWith("]")) {
    try {
      const normalized = incident.replace(/'/g, '"');
      const parsed = JSON.parse(normalized);
      if (Array.isArray(parsed)) {
        if (parsed.length > 0 && typeof parsed[0] === "object" && parsed[0] != null) {
          return parsed
            .filter((item: { createTask?: unknown }) => Boolean(item?.createTask))
            .map((item: { value?: unknown }) => item?.value)
            .filter((x): x is string | number | boolean => x != null)
            .map((x) => String(x).trim())
            .filter(Boolean);
        }
        return parsed.map((x) => String(x).trim()).filter(Boolean);
      }
    } catch {
      // ignore and use fallback parser
    }
  }

  return incident
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .split(",")
    .map((x) => x.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean);
}

export function findMatchingIncidentValue(
  question: Question,
  answer: AnswerLike
): string | null {
  const incidentValues = parseIncidentValues(question.incident);
  if (incidentValues.length === 0) return null;

  const answerCandidates = getAnswerValueCandidates(question, answer);
  for (const candidate of answerCandidates) {
    const normalizedCandidate = normalizeAnswerCompareValue(candidate);
    const matched = incidentValues.find(
      (incidentValue) => normalizeAnswerCompareValue(incidentValue) === normalizedCandidate
    );
    if (matched) return matched;
  }
  return null;
}

export function isIncidentMatched(question: Question, answer: AnswerLike): boolean {
  return findMatchingIncidentValue(question, answer) != null;
}

/** Zapne alarm, pokud odpověď odpovídá incidentu, který obsahuje @. */
export function shouldAutoEnableAlarm(question: Question, answer: AnswerLike): boolean {
  if (!question.attach_alert || answer.skip_check) return false;
  const matchedIncident = findMatchingIncidentValue(question, answer);
  return matchedIncident != null && hasIncidentAutoAlertMarker(matchedIncident);
}
